import { useState, useEffect } from 'react';
import { deriveKey, getKey, idbClearKey, idbGetKey, idbPutKey, setKey } from '../data/crypto.js';
import { fetchAllRows, readCachedRows, sb, verifyKeyAgainst } from '../data/store.js';
import { MedicationApp } from './MedicationApp.jsx';

// Gates the app behind the shared login + encryption passphrase.
export function AuthGate() {
  const [step, setStep] = useState('loading'); // loading | login | passphrase | ready
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      let session = null;
      try { session = (await sb.auth.getSession()).data.session; } catch (e) {}
      if (session) {
        try {
          const k = await idbGetKey();
          if (k) {
            setKey(k);
            let rows;
            try { rows = await fetchAllRows(); } catch (e) { rows = readCachedRows() || []; }
            if (await verifyKeyAgainst(rows)) { setStep('ready'); return; }
            setKey(null); await idbClearKey();
          }
        } catch (e) { setKey(null); }
        setStep('passphrase');
      } else {
        setStep('login');
      }
    })();
  }, []);

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const res = await sb.auth.signInWithPassword({ email: email.trim(), password });
      if (res.error) throw res.error;
      setStep('passphrase');
    } catch (err) {
      setError(err.message || 'Sign-in failed');
    } finally { setBusy(false); }
  };

  const doPass = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      if (!passphrase) throw new Error('Enter a passphrase');
      setKey(await deriveKey(passphrase));
      let rows;
      try { rows = await fetchAllRows(); } catch (e) { rows = readCachedRows() || []; }
      if (!(await verifyKeyAgainst(rows))) {
        setKey(null);
        throw new Error('That passphrase does not match the data on this account.');
      }
      if (remember) { try { await idbPutKey(getKey()); } catch (e) {} }
      else { await idbClearKey(); }
      setStep('ready');
    } catch (err) {
      setError(err.message || 'Could not unlock');
    } finally { setBusy(false); }
  };

  const logout = async () => {
    await idbClearKey();
    try { await sb.auth.signOut(); } catch (e) {}
    localStorage.removeItem('medRowsCache');
    setKey(null);
    setPassword(''); setPassphrase('');
    setStep('login');
  };

  if (step === 'ready') return <MedicationApp onLogout={logout} />;

  const shell = (children) => (
    <div className="min-h-screen p-4 flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800">
      <div className="w-full max-w-sm rounded-2xl shadow-xl p-6 bg-gray-800 border border-gray-700 text-white">
        <h1 className="text-xl font-bold text-center mb-1">MedCalc</h1>
        <p className="text-xs text-center text-gray-400 mb-5">Encrypted shared dosage log</p>
        {children}
      </div>
    </div>
  );
  const inputCls = "w-full px-4 py-3 mb-3 rounded-lg bg-gray-700 border border-gray-600 text-white placeholder-gray-400 focus:ring-2 focus:ring-indigo-500";
  const btnCls = "w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-6 rounded-lg transition disabled:opacity-50";

  if (step === 'loading') return shell(<p className="text-center text-gray-400 text-sm">Loading…</p>);

  if (step === 'login') {
    return shell(
      <form onSubmit={doLogin}>
        <p className="text-sm text-gray-300 mb-3">Sign in with your shared account.</p>
        <input className={inputCls} type="email" placeholder="Shared email" value={email} onChange={e=>setEmail(e.target.value)} autoComplete="username" />
        <input className={inputCls} type="password" placeholder="Password" value={password} onChange={e=>setPassword(e.target.value)} autoComplete="current-password" />
        {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
        <button className={btnCls} disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</button>
      </form>
    );
  }

  return shell(
    <form onSubmit={doPass}>
      <p className="text-sm text-gray-300 mb-3">Enter the shared <strong>encryption passphrase</strong>. It never leaves your phone and is what keeps the data private — if you both forget it, the data can't be recovered.</p>
      <input className={inputCls} type="password" placeholder="Encryption passphrase" value={passphrase} onChange={e=>setPassphrase(e.target.value)} autoComplete="off" />
      <label className="flex items-center gap-2 text-sm text-gray-300 mb-3">
        <input type="checkbox" checked={remember} onChange={e=>setRemember(e.target.checked)} />
        Remember on this phone
      </label>
      {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
      <button className={btnCls} disabled={busy}>{busy ? 'Unlocking…' : 'Unlock'}</button>
      <button type="button" onClick={logout} className="w-full mt-2 text-xs text-gray-400 underline">Sign out</button>
    </form>
  );
}
